import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, saveSessionToken } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { hideBanner, showInterstitialNow } from '../lib/admob';
import { Loader2 } from 'lucide-react';

/**
 * Detect whether we're running inside Chrome (not inside the Matbakhi
 * Capacitor app). Capacitor exposes `window.Capacitor.isNativePlatform()`
 * which is `false` everywhere except inside the app.
 */
function isAndroidChromeOutsideApp() {
  try {
    const isNativeCapacitor = window.Capacitor?.isNativePlatform?.();
    if (isNativeCapacitor) return false;
    return /Android/i.test(navigator.userAgent || '');
  } catch {
    return false;
  }
}

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate('/login'); return; }
    const sessionId = match[1];

    // If we're inside Chrome Custom Tabs on Android (after Google sign-in),
    // we DO NOT want to complete the auth in Chrome — that would log the
    // user into the *browser* and leave them stranded. Fire an Android
    // intent URL instead: it asks Android "open com.matbakhi.app with
    // this URL", which triggers the deep-link intent filter and resumes
    // the app where it left off. The app's `appUrlOpen` listener then
    // picks up the session_id and finishes sign-in.
    if (isAndroidChromeOutsideApp()) {
      const fallback = encodeURIComponent('https://cook-smart-40.emergent.host/');
      const intentUrl =
        `intent://cook-smart-40.emergent.host/#session_id=${sessionId}` +
        `#Intent;scheme=https;package=com.matbakhi.app;` +
        `S.browser_fallback_url=${fallback};end;`;
      window.location.href = intentUrl;
      return;
    }

    (async () => {
      try {
        const { data } = await api.post('/auth/session', { session_id: sessionId });
        if (data?.session_token) saveSessionToken(data.session_token);
        setUser(data.user);
        // Tear down login-screen banner (if it was still alive across the
        // OAuth redirect) before showing the welcome interstitial.
        await hideBanner().catch(() => {});
        showInterstitialNow({ isPremium: !!data.user?.is_premium }).catch(() => {});
        window.history.replaceState(null, '', '/');
        navigate('/', { replace: true, state: { user: data.user } });
      } catch (e) {
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F6F0]" data-testid="auth-callback">
      <Loader2 className="h-8 w-8 animate-spin text-[#C05A42]" />
    </div>
  );
}
